/**
 * NDASignModal — confidentiality acknowledgement modal on the booking
 * checkout flow.
 *
 * BR-checkout-ack rewrite (Owner: Ankit):
 *   1. Auth is checked when the modal renders, not (only) at submit time.
 *      If the user isn't signed in, the action area renders a "Log in to
 *      continue" CTA instead of "I agree". The acknowledgement text
 *      stays visible so the user can still read what they're signing.
 *   2. The raw `fetch` call has been replaced with the shared axios `api`
 *      client. The client's 401-refresh interceptor now handles expired
 *      sessions instead of leaking `{"detail":"Not authenticated"}` to
 *      the DOM. Every catch path also wraps via ApiError so no raw
 *      response body is ever rendered.
 *   3. State machine: idle | submitting | unauthenticated | session_expired
 *      | wrong_account | version_mismatch | success | error_other. Each
 *      state renders its own action area with friendly copy + the
 *      correct primary CTA.
 *   4. Audit trail integrity: the modal sends `content_hash` (SHA-256 of
 *      the rendered policy markdown) + `content_version` so the backend
 *      can detect drift between page-load and submit (responds with 409
 *      and `code='content_version_mismatch'` — we render the
 *      version_mismatch state and offer "Reload to read the new text").
 *   5. Telemetry: emits checkout_ack_viewed / login_prompted /
 *      login_completed / submitted / submit_failed / session_expired.
 *   6. Gated by feature flag `checkout_ack_auth_gate_v2`. The legacy code
 *      path is preserved in git history (commit before this rewrite) so
 *      we can revert via flag if anything regresses.
 *
 * Props:
 *   bookingId       — the booking we're gating
 *   open            — modal visibility
 *   onClose         — user cancelled
 *   onSigned        — user signed; gives parent the acceptance_id
 */
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { X, ShieldCheck, AlertTriangle, LogIn, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/api";
import { ApiError, classifyAckError } from "@/lib/apiError";
import { isFlagOn } from "@/lib/flags";
import { track } from "@/lib/telemetry";
import { useAuth } from "@/App";
// Real per-booking NDA (was pointed at recording_consent — a consent notice
// with no confidentiality obligations; see migration 047).
import ndaContent from "@/legal-rendered/nda";

// BR-checkout-ack — must equal `legal_audit.policy_versions.version` for the
// currently-effective `nda_v1` row, or backend sign_nda 409s on the version
// check (routes/legal.py). Migration 047 sets that row's version to '1.1.0'.
// (The previous value "nda_v1" was the policy_id, not the version — it never
// matched the DB's "1.0.0" and would have 409'd a real signer.)
const NDA_CONTENT_VERSION = "1.1.0";

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function renderMarkdown(md) {
  const lines = md.split("\n");
  const blocks = [];
  let buffer = [];
  const flush = (k) => {
    if (!buffer.length) return;
    const text = buffer.join(" ").trim();
    if (text) blocks.push(<p key={k} className="mb-3 text-sm text-slateline-700 leading-relaxed">{text}</p>);
    buffer = [];
  };
  lines.forEach((rawLine, i) => {
    const line = rawLine.replace(/\r$/, "");
    if (line.startsWith("# ") || line.startsWith("## ")) {
      flush(`p${i}`);
      blocks.push(<h3 key={`h${i}`} className="font-display text-base mt-4 mb-2 text-ink">{line.replace(/^#+ /, "")}</h3>);
    } else if (line.trim() === "") {
      flush(`p${i}`);
    } else {
      buffer.push(line);
    }
  });
  flush("end");
  return blocks;
}

async function sha256Hex(text) {
  // Browser-native SHA-256 via Web Crypto. Works in every modern target.
  // We don't fall back if it's unavailable — let the request go without a
  // hash and rely on backend's tolerance during the rollout window.
  if (typeof crypto === "undefined" || !crypto.subtle) return null;
  const enc = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function loginReturnTo(bookingId) {
  const path = `/booking/${bookingId}/checkout`;
  return `/login?return_to=${encodeURIComponent(path)}`;
}

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

export default function NDASignModal({ bookingId, open, onClose, onSigned }) {
  const { status: authStatus, refresh: refreshAuth } = useAuth();
  const flagOn = isFlagOn("checkout_ack_auth_gate_v2");

  // Modal-internal state machine. The `auth` states (unauthenticated /
  // session_expired) override the user's actions even after `idle` —
  // the submit guard checks live auth too as defence in depth (§5.3).
  const [state, setState] = useState("idle");
  // Optional displayable error for the `error_other` state. Always a
  // friendly string from ApiError.message, never raw server data.
  const [errorMessage, setErrorMessage] = useState(null);

  const blocks = useMemo(() => renderMarkdown(ndaContent), []);
  const pageLoadAt = useRef(Date.now());

  // ── Effect: telemetry on view + reset state when modal opens/closes ────
  useEffect(() => {
    if (!open) return;
    pageLoadAt.current = Date.now();
    track("checkout_ack_viewed", {
      booking_id: bookingId,
      auth_status: authStatus,
      content_version: NDA_CONTENT_VERSION,
    });
    if (authStatus === "unauthenticated") {
      track("checkout_ack_login_prompted", { booking_id: bookingId });
    }
    // Reset transient error state on each open so a previous failure
    // doesn't follow the user across cancels/reopens.
    setErrorMessage(null);
    if (state === "success") setState("idle"); // re-open after a previous sign would be weird, normalise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Effect: Esc closes ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && state !== "submitting") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, state, onClose]);

  // ── Submit handler ─────────────────────────────────────────────────────
  const handleSign = useCallback(async () => {
    // §5.3 — submit-time guard. Even though the button shouldn't appear
    // when unauthed, double-check live auth state in case a tab regained
    // focus during a session expiry race.
    if (authStatus === "unauthenticated") {
      setState("unauthenticated");
      track("checkout_ack_login_prompted", { booking_id: bookingId });
      return;
    }

    setState("submitting");
    setErrorMessage(null);
    try {
      const content_hash = await sha256Hex(ndaContent);
      const { data } = await api.post(`/legal/nda`, {
        booking_id: bookingId,
        content_hash,
        content_version: NDA_CONTENT_VERSION,
      });
      track("checkout_ack_submitted", {
        booking_id: bookingId,
        content_version: NDA_CONTENT_VERSION,
        auto_retry: false,
      });
      setState("success");
      onSigned(data);
    } catch (err) {
      const e = ApiError.from(err);
      track("checkout_ack_submit_failed", {
        booking_id: bookingId,
        status_code: e.status,
        error_category: e.kind,
      });
      const next = classifyAckError(e);
      if (next === "session_expired") {
        // §5.5 — when the user re-logs from this state we auto-retry the
        // submit. We don't refresh auth from here; the api client's
        // interceptor already tried + failed (otherwise we wouldn't be here).
        track("checkout_ack_session_expired", {
          booking_id: bookingId,
          seconds_since_page_load: Math.round(
            (Date.now() - pageLoadAt.current) / 1000
          ),
        });
      }
      if (next === "error_other") {
        // Only the catch-all needs a displayable detail. Other states
        // own their own copy via renderActionArea.
        setErrorMessage(e.message);
      }
      setState(next);
    }
  }, [authStatus, bookingId, onSigned]);

  // ── Effect: post-login auto-retry semantics (§5.5) ─────────────────────
  // If the user was in `session_expired` and just transitioned back to
  // authenticated (e.g. via the api client's refresh-on-401 succeeding on
  // some unrelated request), auto-resubmit. Re-authenticating from the
  // `unauthenticated` state does NOT auto-submit — the user must click
  // "I agree" explicitly so the audit trail is tied to a real click.
  useEffect(() => {
    if (state === "session_expired" && authStatus === "authenticated") {
      track("checkout_ack_login_completed", {
        booking_id: bookingId,
        seconds_to_login: Math.round(
          (Date.now() - pageLoadAt.current) / 1000
        ),
      });
      // The next paint cycle is fine; no need to defer further.
      handleSign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, state]);

  // ── Effect: page-load auth gate (§5.2) ─────────────────────────────────
  // When the modal opens and auth is unauthenticated, transition the modal
  // into the unauthenticated state so the action area renders "Log in to
  // continue" instead of "I agree". Don't redirect — the user must be able
  // to read the policy before logging in.
  useEffect(() => {
    if (!open || !flagOn) return;
    if (authStatus === "unauthenticated" && state === "idle") {
      setState("unauthenticated");
    }
    if (authStatus === "authenticated" && state === "unauthenticated") {
      // Just logged in from this modal — drop back to idle so "I agree"
      // appears. Does NOT auto-submit (per §5.5: post-unauth re-login is
      // an explicit click).
      track("checkout_ack_login_completed", {
        booking_id: bookingId,
        seconds_to_login: Math.round(
          (Date.now() - pageLoadAt.current) / 1000
        ),
      });
      setState("idle");
    }
  }, [authStatus, state, open, flagOn, bookingId]);

  if (!open) return null;

  // ──────────────────────────────────────────────────────────────────────
  // Render the action area for the current state. Action area swaps;
  // policy text stays identical so the user can always read what they're
  // about to sign.
  // ──────────────────────────────────────────────────────────────────────

  const renderActionArea = () => {
    if (state === "success") {
      return (
        <div className="flex items-center gap-2 text-brand-green" aria-live="polite">
          <ShieldCheck className="w-4 h-4" />
          <span className="text-sm font-medium">Recorded. Continuing…</span>
        </div>
      );
    }

    if (state === "unauthenticated") {
      return (
        <div className="space-y-3" aria-live="polite">
          <p className="text-sm text-slateline-600">
            You need to be signed in to acknowledge this. Once you log in,
            your acceptance will be recorded against your account.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-md"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { window.location.href = loginReturnTo(bookingId); }}
              className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Log in to continue
            </Button>
          </div>
        </div>
      );
    }

    if (state === "session_expired") {
      return (
        <div className="space-y-3">
          <div
            role="alert"
            className="flex items-start gap-2 p-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-800"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              Your session has expired. Please log in again to sign this acknowledgement.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="rounded-md">
              Cancel
            </Button>
            <Button
              onClick={() => { window.location.href = loginReturnTo(bookingId); }}
              className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Log in
            </Button>
          </div>
        </div>
      );
    }

    if (state === "wrong_account") {
      return (
        <div className="space-y-3">
          <div
            role="alert"
            className="flex items-start gap-2 p-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-800"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              This booking belongs to a different account. Please log in with the
              account that made the booking.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="rounded-md">
              Cancel
            </Button>
            <Button
              onClick={() => { window.location.href = loginReturnTo(bookingId); }}
              className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Log out and switch accounts
            </Button>
          </div>
        </div>
      );
    }

    if (state === "version_mismatch") {
      return (
        <div className="space-y-3">
          <div
            role="alert"
            className="flex items-start gap-2 p-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-800"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              The acknowledgement text was updated while you were reading.
              Please reload to read the new version before signing.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="rounded-md">
              Cancel
            </Button>
            <Button
              onClick={() => window.location.reload()}
              className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload
            </Button>
          </div>
        </div>
      );
    }

    if (state === "error_other") {
      return (
        <div className="space-y-3">
          <div
            role="alert"
            className="flex items-start gap-2 p-3 rounded-sm border border-red-300 bg-red-50 text-red-800"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              {errorMessage || "Something went wrong saving your acknowledgement."}{" "}
              If this keeps happening, contact <a href="mailto:support@xpert.company" className="underline">support@xpert.company</a>.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="rounded-md">
              Cancel
            </Button>
            <Button
              onClick={handleSign}
              className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md"
            >
              Try again
            </Button>
          </div>
        </div>
      );
    }

    // idle | submitting — the happy path.
    return (
      <div>
        <p className="text-xs text-slateline-500 mb-4">
          Clicking <span className="italic text-brand-green">I agree</span> records your
          acceptance with the current date, your IP address, and a cryptographic hash of
          this text — proving what you actually saw on this screen.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={state === "submitting"}
            className="rounded-md"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSign}
            disabled={state === "submitting"}
            className="bg-brand-green hover:bg-brand-green/90 text-white rounded-md"
          >
            {state === "submitting" ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Recording…
              </>
            ) : (
              "I agree"
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="nda-modal-title"
        className="bg-canvas border border-slateline-200 rounded-md shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slateline-200">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-brand-green" />
            <div>
              <h2 id="nda-modal-title" className="font-display text-xl">
                Confidentiality acknowledgement
              </h2>
              <p className="text-sm text-slateline-500">
                Required before your session can be confirmed
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={state === "submitting"}
            aria-label="Close acknowledgement modal"
            className="text-slateline-500 hover:text-ink"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body — policy text. Stays identical across all states
            so the user can always read what they're signing. */}
        <div className="overflow-y-auto p-6 flex-1">
          <div className="prose max-w-none">{blocks}</div>
        </div>

        {/* Footer — action area swaps per state */}
        <div className="p-6 border-t border-slateline-200 bg-canvas-card">
          {renderActionArea()}
        </div>
      </div>
    </div>
  );
}

export { NDASignModal };
